<script setup>
import InputField from "@/components/forms/InputField.vue";

const props = defineProps({
    dataTest: {
        type: String,
        default: "",
    },
    namedPricingAllowed: {
        type: Boolean,
        default: true,
    },
    namedPriceList: {
        type: Array,
        default: () => [],
    },
    hasNamedPriceList: {
        type: Boolean,
        default: false,
    },
    basePrice: {
        type: String,
        default: "",
    },
    basePriceError: {
        type: String,
        default: "",
    },
    namedPriceError: {
        type: String,
        default: "",
    },
});

const emit = defineEmits([
    "update:namedPriceList",
    "update:hasNamedPriceList",
    "update:basePrice",
]);

const addNamedPrice = (defaultValue = "") => {
    const newNamedPriceElments = [];
    newNamedPriceElments.push({
        name: "",
        value: defaultValue,
    });
    if (props.namedPriceList.length === 0) {
        newNamedPriceElments.push({
            name: "",
            value: "",
        });
    }
    emit("update:namedPriceList", [...props.namedPriceList, ...newNamedPriceElments]);
};

const changeToNamedPrice = () => {
    emit("update:hasNamedPriceList", true);
    addNamedPrice(props.basePrice);
    emit("update:basePrice", "");
};

const removeNamedPrice = (index) => {
    if (props.namedPriceList.length === 2) {
        if (props.namedPriceList[0].value !== "") {
            emit("update:basePrice", props.namedPriceList[0].value);
        }
        emit("update:hasNamedPriceList", false);
        emit("update:namedPriceList", []);
        return;
    }
    const filteredList = props.namedPriceList.filter((_, i) => i !== index);
    emit("update:namedPriceList", filteredList);
};

const updateBasePrice = (value) => {
    emit("update:basePrice", value);
};

const updateNamedPrice = (value, name, index) => {
    const updatedNamedPriceList = [...props.namedPriceList];
    updatedNamedPriceList[index] = {
        name,
        value,
    };
    emit("update:namedPriceList", updatedNamedPriceList);
};

const getNamedPriceError = (type, index) => props.namedPriceError[index]?.[type] || "";

</script>

<template>
    <div>
        <div v-if="!hasNamedPriceList" class="d-flex align-items-baseline flex-column">
            <InputField
                inputClass="min-w-300"
                :data-test="props.dataTest"
                :value="basePrice"
                @update:modelValue="updateBasePrice"
                labelText="Price (including VAT)"
                placeholder="Enter a price"
                required
                type="price"
                :error="basePriceError"
                min="0"
                max="1000"
            />
            <button
                v-if="namedPricingAllowed"
                class="add-another-price-button text-purple"
                @click="changeToNamedPrice"
            >
                + Add another price
            </button>
        </div>
        <div v-if="hasNamedPriceList">
            <div class="d-flex flex-row flex-gap-1">
                <div class="min-w-300">
                    <label class="named-price-label" :for="props.id + '0-name'">
                        Price (including VAT)
                        <sup class="required-field">*</sup>
                    </label>
                </div>
                <div class="flex-grow-1 min-w-300">
                    <label class="named-price-label" :for="props.id + '0-price'">
                        Ticket Name
                        <sup class="required-field">*</sup>
                    </label></div>
            </div>
            <div
                v-for="(namedPrice, index) in namedPriceList" :key="index"
                :class="['d-flex flex-row flex-gap-1 ', {
                    'mt-2': index !== 0
                }]"
                class="d-flex flex-row flex-gap-1 "
            >
                <div class="flex-grow-1 min-w-300">
                    <InputField
                        :data-test="props.dataTest + index + '-price'"
                        :id="props.id + index + '-value'"
                        type="price"
                        :value="namedPrice.value"
                        @update:modelValue="value => updateNamedPrice(
                            value,
                            namedPrice.name,
                            index
                        )"
                        isFullWidth
                        placeholder="Enter a price"
                        :error="getNamedPriceError('value',index)"
                        min="0"
                        max="1000"
                    />
                </div>
                <div class="flex-grow-1 min-w-300">
                    <InputField
                        :data-test="props.dataTest + index + '-name'"
                        :id="props.id + index + '-name'"
                        type="text"
                        :value="namedPrice.name"
                        @update:modelValue="name => updateNamedPrice(namedPrice.value, name, index)"
                        isFullWidth
                        placeholder="E.g. Adult"
                        :error="getNamedPriceError('name',index)"
                    />
                </div>
                <div class="d-flex align-items-baseline justify-content-center flex-grow-0">
                    <button
                        :class="[
                            {'no-visible-button': index === 0},
                            'remove-advanced-price-button mt-2'
                        ]"
                        :disabled="index === 0"
                        @click="() => removeNamedPrice(index)"
                    >
                        X
                    </button>
                </div>
            </div>
            <button class="add-another-price-button text-purple" @click="() => addNamedPrice()">
                + Add another price
            </button>
        </div>
    </div>
</template>

<style scoped>
label, .label {
    font-weight: 600;
    visibility: visible !important;
    opacity: 1 !important;
    font-size: 12px;
}

button.add-another-price-button,
button.add-another-price-button:hover,
button.add-another-price-button:focus,
button.add-another-price-button:active,
button.add-another-price-button:visited {
    background-color: unset;
    border: 1px solid transparent;
    color: #333;
    cursor: pointer;
    font-size: 14px;
    margin-top: 10px;
    padding: 4px 20px 10px 0;
    font-weight: 600;
    transition: none;
    outline: none;
}

button.remove-advanced-price-button,
button.remove-advanced-price-button:hover,
button.remove-advanced-price-button:focus,
button.remove-advanced-price-button:active,
button.remove-advanced-price-button:visited {
    background-color: unset;
    border: 1px solid transparent;
    color: red;
    cursor: pointer;
    font-size: 14px;
    padding: 5px;
    font-weight: 600;
    transition: none;
    outline: none;
}

.flex-gap-1 {
    gap: 1rem;
}

button.remove-advanced-price-button.no-visible-button {
    opacity: 0;
    cursor: unset;
}

.mw-45 {
    max-width: 45%;
}

.min-w-300{
    min-width: 300px;
}

.named-price-label{
    font-weight: 600;
    font-size: 12px;
}

.required-field {
    color: #ed5a5e;
    margin-left: -2px;
}

button.add-another-price-button.text-purple {
    color: rgba(38, 0, 69, 1)!important;
}
</style>
